import {
  appConfigResponseSchema,
  type AdminFactoryListResponse,
  type AdminFactoryReplyView,
  type FactoryInput,
  type FactoryReply,
  type FactoryReplyBulkInviteResult,
  type FactoryReplyInviteCreated,
  type FactoryView,
} from '@repo/shared'
import { useEffect, useState } from 'react'
import { json, request } from './api'
import { env } from './env'
import type { GetToken } from './lib/auth'

/** 공장 목록 관리자 API (로드맵 1위). api.ts 를 덜 고치려고 따로 둔다 (cases-api.ts 와 같은 방식) */
export const factoriesApi = {
  list: (getToken: GetToken) => request<AdminFactoryListResponse>(getToken, '/admin/factories'),
  create: (getToken: GetToken, input: FactoryInput) =>
    request<{ factory: FactoryView }>(getToken, '/admin/factories', json('POST', input)),
  update: (getToken: GetToken, id: string, input: FactoryInput) =>
    request<{ factory: FactoryView }>(
      getToken,
      `/admin/factories/${encodeURIComponent(id)}`,
      json('PUT', input),
    ),
  // ── 공장 회신 받기 (로드맵 8번). 스위치가 꺼져 있으면 서버가 404 ──
  createInvite: (getToken: GetToken, id: string, language: 'ko' | 'en') =>
    request<FactoryReplyInviteCreated>(
      getToken,
      `/admin/factories/${encodeURIComponent(id)}/reply-invites`,
      json('POST', { language }),
    ),
  importCapabilities: (getToken: GetToken, id: string, reply: FactoryReply) =>
    request<{ factory: FactoryView }>(
      getToken,
      `/admin/factories/${encodeURIComponent(id)}/capabilities`,
      json('PUT', reply),
    ),
  bulkInvites: (getToken: GetToken, factoryIds: string[], language: 'ko' | 'en') =>
    request<FactoryReplyBulkInviteResult>(
      getToken,
      '/admin/factory-reply-invites',
      json('POST', { factoryIds, language }),
    ),
  pendingReplies: (getToken: GetToken) =>
    request<{ items: AdminFactoryReplyView[] }>(getToken, '/admin/factory-replies'),
  applyReply: (getToken: GetToken, id: string) =>
    request<{ ok: true }>(
      getToken,
      `/admin/factory-replies/${encodeURIComponent(id)}/apply`,
      json('POST', {}),
    ),
  rejectReply: (getToken: GetToken, id: string) =>
    request<{ ok: true }>(
      getToken,
      `/admin/factory-replies/${encodeURIComponent(id)}/reject`,
      json('POST', {}),
    ),
}

/**
 * 공장 회신 받기가 열려 있는지: 서버 코드(GET /config features.factoryReplies)와 관리자 설정이 둘 다 참일 때만.
 * 옛 서버면 칸이 없어 꺼진 것으로 본다 (useManufacturingFeature 와 같은 방식)
 */
export function useFactoryRepliesFeature() {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    let alive = true
    void fetch(`${env.API_URL}/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((raw) => {
        const c = appConfigResponseSchema.parse(raw)
        if (alive)
          setEnabled(c.features?.factoryReplies === true && c.settings.factoryReplies.enabled)
      })
      .catch(() => {
        if (alive) setEnabled(false)
      })
    return () => {
      alive = false
    }
  }, [])
  return enabled
}
